/**
 * NewsRoom Page Component - By John Bellew - Infinia.ie - last updated 11/07/2025
 * 
 * A comprehensive news room page with advanced features:
 * - Unified news display with weekly and promoted content
 * - Smart date parsing for multiple date formats
 * - News filtering by date ranges and categories
 * - Modal integrations for article and news submission
 * - Featured articles and news table modals
 * - Research centre integration
 * - External sources and categories display
 * - Robust date sorting and filtering logic
 * 
 * Features:
 * - Real-time news data fetching and caching
 * - Intelligent weekly news filtering
 * - Monthly and yearly news aggregation
 * - Date format normalization and parsing
 * - Intro news detection and highlighting
 * - Submit article and news functionality
 * - Table view modals for comprehensive browsing
 * - Responsive design for various screen sizes
 * 
 * @returns {JSX.Element} NewsRoom page component with comprehensive news management
 */
import React, { useState, useEffect } from 'react';
import UnifiedNews from '../components/NewsRoom/UnifiedNews';
import Divider from '../components/Common/Divider';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { getToday } from '../utils/textUtils';
import CategoriesAndLinks from '../components/NewsRoom/CategoriesAndLinks';
import ResearchCentre from '../components/NewsRoom/ResearchCentre';
import SubmitArticleModal from '../modals/SubmitArticleModal';
import SubmitNewsModal from '../modals/SubmitNewsModal';
import NewsTableModal from '../modals/NewsTableModal';
import FeaturedArticlesTableModal from '../modals/FeaturedArticlesTableModal';
import './NewsRoom.css';
import '../css/Colors.css';

const { FILE_NAMES } = require('../utils/constants');

/**
 * NewsRoom component for displaying and managing news content
 * with advanced filtering, sorting, and submission capabilities
 */
function NewsRoom() {
  //Data
  const [weeklyNewsData, setWeeklyNewsData] = useState([]);
  const [promotedNewsData, setpromotedNewsData] = useState([]);
  const [sortedPromotedNewsData, setSortedPromotedNewsData] = useState([]);
  const [allNewsData, setAllNewsData] = useState([]);
  const [combinedNewsHubData, setCombinedNewsHubData] = useState([]);
  const [weeklyFilteredNews, setWeeklyFilteredNews] = useState([]);
  const [weeklyIntroNews, setWeeklyIntroNews] = useState(null);
  const [externalSourcesCategoriesData, setExternalSourcesCategoriesData] = useState([]);
  const [externalSourcesLinksData, setExternalSourcesLinksData] = useState([]);
  const [showArticleSubmitModal, setShowArticleSubmitModal] = useState(false);
  const [showNewsSubmitModal, setShowNewsSubmitModal] = useState(false);
  const [showNewsTableModal, setShowNewsTableModal] = useState(false);
  const [showFeaturedArticlesTableModal, setShowFeaturedArticlesTableModal] = useState(false);

  const handleShowArticleSubmitModal = () => {
    setShowArticleSubmitModal(true);
  };

  const handleCloseArticleSubmitModal = () => {
    setShowArticleSubmitModal(false);
  };

  const handleShowNewsSubmitModal = () => {
    setShowNewsSubmitModal(true);
  };
  
  const handleCloseNewsSubmitModal = () => {
    setShowNewsSubmitModal(false);
  };

  const handleShowNewsTableModal = () => {
    setShowNewsTableModal(true);
  };
  
  const handleCloseNewsTableModal = () => {
    setShowNewsTableModal(false);
  };

  const handleShowFeaturedArticlesTableModal = () => {
    setShowFeaturedArticlesTableModal(true);
  };
  
  const handleCloseFeaturedArticlesTableModal = () => {
    setShowFeaturedArticlesTableModal(false);
  };

  // Robust date parsing function for multiple data sources
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0); // Return epoch for empty dates (sorts to bottom)
    
    try {
      // Handle ISO string format (2025-02-13T00:00:00.000Z)
      if (dateStr.includes('T')) {
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      // Handle YYYY-MM-DD format (most common)
      if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
        const parsed = new Date(dateStr + 'T00:00:00.000Z');
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      // Handle DD/MM/YYYY format (European)
      if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const parts = dateStr.split('/');
        // Try DD/MM/YYYY first (more common in your context)
        if (parts[0] <= 31 && parts[1] <= 12) {
          const [day, month, year] = parts;
          const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
          if (!isNaN(parsed.getTime())) return parsed;
        }
        // Fallback to MM/DD/YYYY
        const [month, day, year] = parts;
        const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      // Handle DD-MM-YYYY format
      if (dateStr.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        const [day, month, year] = dateStr.split('-');
        const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      // Try direct parsing as fallback
      const parsed = new Date(dateStr);
      return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      
    } catch (error) {
      return new Date(0); // Return epoch for invalid dates
    }
  };

  const sortByPortalPublishDate = (data) => {
    return [...data].sort((a, b) => {
      // Use the robust date parsing function
      const dateA = parseDate(a.portalPublishDate);
      const dateB = parseDate(b.portalPublishDate);
      
      // Sort in descending order (newest first)
      return dateB.getTime() - dateA.getTime();
    });
  };

  // Extract intro search logic to a separate function
  const findIntroNews = (data) => {
    if (!data || data.length === 0) return null;
    
    // Try multiple ways to find the intro
    let intro = data.find(newsItem => newsItem.weeklyCategory === 'Intro') ||
                data.find(newsItem => newsItem.weeklyCategory === 'intro') ||
                data.find(newsItem => newsItem.title?.toLowerCase().includes('intro')) ||
                data.find(newsItem => newsItem.title === 'Intro');
                
    if (intro) {
      intro.image = "/assets/img/news/weekly/Pat.png"; 
    }
    
    return intro;
  };

  const filterNewsForThisWeek = (data, dayOfWeek) => {
    const today = new Date();
    const currentDayOfWeek = today.getDay();
    
    // Make it very inclusive - go back 14 days from today to capture recent content
    let daysDifference = 14; // Look back 2 weeks to be very inclusive
    
    const lastSpecificDay = new Date(today);
    lastSpecificDay.setDate(today.getDate() - daysDifference);
    lastSpecificDay.setHours(0, 0, 0, 0);
    
    const endDate = getToday();
    
    // Date filtering is working - debug logs removed
    
    const filteredData = data.filter(newsItem => {
      // Check if portalPublishDate exists
      if (!newsItem.portalPublishDate) {
        return false;
      }
      
      // Use the existing robust parseDate function instead of manual parsing
      const newsDate = parseDate(newsItem.portalPublishDate);
      
      // Skip items with invalid dates
      if (!newsDate || isNaN(newsDate.getTime())) {
        return false;
      }
      // Set time to start of day for accurate comparison
      newsDate.setHours(0, 0, 0, 0);
      const isInRange = newsDate >= lastSpecificDay && newsDate <= endDate;
      return isInRange;
    });
    
    // Filtering results are working - debug logs removed
    
    return sortByPortalPublishDate(filteredData);
  };

  const getMonthlyNews = (data) => {
    return sortByPortalPublishDate([...data]).slice(0, 30);
  };

  useEffect(() => {
    // Filter news from a specific day of the week
    let thisWeeksNews = filterNewsForThisWeek(weeklyNewsData, 5); // 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
    let thisMonthNews = getMonthlyNews(weeklyNewsData);
    let newsData = weeklyNewsData;

    // If we have less than 5 articles from this week, include some from this month
    if (thisWeeksNews.length < 5) {
      // Including monthly news to supplement weekly news
      // Get articles from this month that aren't already in thisWeeksNews
      const weeklyNewsIds = new Set(thisWeeksNews.map(item => item.id));
      const additionalNews = thisMonthNews.filter(item => !weeklyNewsIds.has(item.id));
      
      // Combine weekly and monthly news, but prioritize weekly news
      newsData = [...thisWeeksNews, ...additionalNews].slice(0, 10); // Show up to 10 articles total
    } else {
      newsData = thisWeeksNews;
    }

    // Find intro from the filtered news data first
    const intro = findIntroNews(newsData);
    setWeeklyIntroNews(intro);

    // Remove intro from filtered news since it's handled separately
    const nonIntroNews = newsData.filter(newsItem => newsItem.weeklyCategory !== 'Intro');
    
    // Sort the non-intro news by portalPublishDate
    const sortedNonIntroNews = sortByPortalPublishDate([...nonIntroNews]);
    
    // Show only the first 20 items in the slider
    setWeeklyFilteredNews(sortedNonIntroNews.slice(0, 20));
  }, [weeklyNewsData]);

  // Sort promoted news data when it's loaded
  useEffect(() => {
    if (promotedNewsData.length > 0) {
      const sortedData = sortByPortalPublishDate(promotedNewsData);
      // Show only the first 20 items in the slider
      setSortedPromotedNewsData(sortedData.slice(0, 20));
    }
  }, [promotedNewsData]);

  // Combine all news data for the News Hub
  useEffect(() => {
    if (allNewsData.length > 0 || promotedNewsData.length > 0) {
      const newsWithSource = allNewsData.map(item => ({
        ...item,
        sourceType: 'news',
        issueDate: item.issueDate || item.portalPublishDate || ''
      }));
      
      const promotedWithSource = promotedNewsData.map(item => ({
        ...item,
        sourceType: 'featured',
        issueDate: item.issueDate || item.portalPublishDate || ''
      }));
      
      const combined = [...newsWithSource, ...promotedWithSource];
      const sortedCombined = sortByPortalPublishDate(combined);
      setCombinedNewsHubData(sortedCombined);
    }
  }, [allNewsData, promotedNewsData]);

  useEffect(() => {
    const fetchFileInfoAndContent = async () => {
      try {
        // WeeklY News
        const newsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MONTHLY_NEWS);
        setWeeklyNewsData(newsResult.fileContent);
        
        // Featured Topics
        const promotedTopicsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.FEATURED_AND_SUBMITTED_ARTICLES);
        setpromotedNewsData(promotedTopicsResult.fileContent);
        
        //Research Center 
        const allNewsDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.NEWS);
        setAllNewsData(allNewsDataResult.fileContent);
        
        // External Sources Categories & Links
        const externalSourcescategoriesResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EXTERNAL_INFO_SOURCES_CATEGORIES);
        setExternalSourcesCategoriesData(externalSourcescategoriesResult.fileContent);
        
        const externalSourcesResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EXTERNAL_INFO_SOURCES);
        setExternalSourcesLinksData(externalSourcesResult.fileContent);

      } catch (err) {
        console.error('Error fetching news room data:', err);
      }
    };

    fetchFileInfoAndContent();
  }, []);



  return (
    <div className='totalPage'>

      <section id="weeklynews" className="wrapper">
        <div className="container mt-6 pb-6">
          <h2 className="text-uppercase main-header">Latest News</h2>
        </div>
        <div className="container">
          {weeklyNewsData.length > 0 ? (
            <UnifiedNews data={weeklyFilteredNews} introData={weeklyIntroNews} showTags={true} autoPlay={true} />
          ) : (
            <LoadingSpinner text="Loading News" />
          )}
          <div className='mt-10 text-right'>
            <button 
              className="btn btn-compact mr-3" 
              onClick={handleShowNewsTableModal}
              title="Discover more Latest News"
              style={{ 
                background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
                transform: 'translateY(0px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(45deg, #764ba2 0%, #667eea 100%)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)';
                e.target.style.transform = 'translateY(0px)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              <i className="fas fa-compass"></i> More...
            </button>
            <button className='btn btn-primary btn-compact' onClick={() => handleShowNewsSubmitModal()}><i className="fas fa-upload"></i> Submit News</button>
          </div>
        </div>
        {showNewsSubmitModal && (
              <SubmitNewsModal show={showNewsSubmitModal} onClose={handleCloseNewsSubmitModal} />
            )}
      </section>

      <Divider height={60} />

      <section id="featuredarticles" className="wrapper bg-gradient-primary">
        <div className="container pb-6">
          <h2 className="text-uppercase main-header">Featured & Submitted Articles</h2>
        </div>
        <div className="container">
          {sortedPromotedNewsData.length > 0 ? (
            <UnifiedNews data={sortedPromotedNewsData} showSubmitted={true} autoPlay={true} />
          ) : (
            <LoadingSpinner text="Loading Featured Topics" />
          )}
           <div className='mt-10 text-right'>
            <button 
              className="btn btn-compact mr-3" 
              onClick={handleShowFeaturedArticlesTableModal}
              title="Discover Featured & Submitted Articles"
              style={{ 
                background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
                transform: 'translateY(0px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(45deg, #764ba2 0%, #667eea 100%)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)';
                e.target.style.transform = 'translateY(0px)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              <i className="fas fa-compass"></i> More...
            </button>
            <button className='btn btn-primary btn-compact' onClick={() => handleShowArticleSubmitModal()}><i className="fas fa-upload"></i> Submit Article</button>
            {showArticleSubmitModal && (
              <SubmitArticleModal show={showArticleSubmitModal} onClose={handleCloseArticleSubmitModal} />
            )}
          </div>
        </div>
      </section>

      <Divider height={60} />

      <section className="wrapper bg-gradient-primary">
        <div id="research" className="container pb-6">
          <h2 className="text-uppercase main-header">News Hub</h2>
        </div>
        <div className="container" id="researchCentre">
          {combinedNewsHubData.length > 0 ? (
            <ResearchCentre data={combinedNewsHubData} />
          ) : (
            <LoadingSpinner text="Loading News Hub" />
          )}

        </div>
      </section>

      <Divider height={60} />


      <section className="wrapper bg-gradient-primary">
        <div id="external" className="container pb-6">
          <h2 className="text-uppercase main-header">External Information Sources</h2>
        </div>
        <div className="container">
          <CategoriesAndLinks categoriesData={externalSourcesCategoriesData} linksData={externalSourcesLinksData} />
        </div>
      </section>

      {/* News Table Modal */}
      {showNewsTableModal && (
        <NewsTableModal 
          show={showNewsTableModal} 
          onClose={handleCloseNewsTableModal}
          data={weeklyNewsData}
          title="Latest News"
        />
      )}

      {/* Featured Articles Table Modal */}
      {showFeaturedArticlesTableModal && (
        <FeaturedArticlesTableModal 
          show={showFeaturedArticlesTableModal} 
          onClose={handleCloseFeaturedArticlesTableModal}
          data={sortByPortalPublishDate(promotedNewsData)}
          title="Featured & Submitted Articles"
        />
      )}
    </div>
  );
}

NewsRoom.propTypes = {
  // NewsRoom is a page component with no props currently
};

export default NewsRoom;
